import { createHighlighterCore, type ShikiTransformer } from "shiki/core"
import { createOnigurumaEngine } from "shiki/engine/oniguruma"
import langGo from "shiki/langs/go.mjs"
import themeLight from "shiki/themes/github-light.mjs"
import themeDark from "shiki/themes/github-dark-default.mjs"
import { transformerNotationHighlight } from "@shikijs/transformers"
import { injectStyle, createPageShell } from "./style.js"

const highlighter = await createHighlighterCore({
    themes: [themeLight, themeDark],
    langs: [langGo],
    engine: createOnigurumaEngine(import("shiki/wasm")),
})

function lineMetadataTransformer(): ShikiTransformer {
    let lineCounter = 0
    return {
        pre(node) {
            lineCounter = 0
            node.properties.class = String(node.properties.class ?? "") + " code-viewer"
        },
        line(node) {
            lineCounter++
            node.properties.id = `L${String(lineCounter)}`
            node.properties["data-line"] = lineCounter
        },
    }
}

function renderCode(code: string, lang: string): string {
    return highlighter.codeToHtml(code, {
        lang,
        themes: { light: "github-light", dark: "github-dark-default" },
        cssVariablePrefix: "--shiki-",
        defaultColor: false,
        transformers: [
            transformerNotationHighlight(),
            lineMetadataTransformer(),
        ],
    })
}

// CSS for the viewer, injected once.
const css = `
.code-viewer {
    margin: 0;
    padding: 0;
    overflow-x: auto;
    counter-reset: line;
    outline: none;
    font-family: inherit;
    color: light-dark(var(--shiki-light), var(--shiki-dark));
    background-color: light-dark(var(--shiki-light-bg), var(--shiki-dark-bg));
    flex: 1;
}

.code-viewer code {
    display: block;
    font-family: inherit;
}

.code-viewer .line {
    display: block;
    padding: 0 16px 0 0;
    min-height: 1.5em;
}

.code-viewer .line::before {
    counter-increment: line;
    content: attr(data-line);
    display: inline-block;
    width: 4em;
    text-align: right;
    padding-right: 16px;
    margin-right: 16px;
    color: light-dark(#636c76, #8b949e);
    border-right: 1px solid light-dark(#d0d7de, #30363d);
    user-select: none;
    -webkit-user-select: none;
    cursor: pointer;
}

.code-viewer .line.highlighted {
    background: light-dark(#fff8c5, rgba(187, 128, 9, 0.4));
}

.code-viewer span {
    color: light-dark(var(--shiki-light), var(--shiki-dark));
}

/* Plain text mode: override all syntax colors. */
.page-shell.plain-text .code-viewer span {
    color: inherit !important;
}
`


interface CodeViewerOptions {
    code: string
    filename?: string
    header?: HTMLElement
}

export function createCodeViewer({ code, filename, header }: CodeViewerOptions): HTMLElement {
    injectStyle("code-viewer", css)

    const isGo = filename?.endsWith(".go") ?? false

    // Build the header element if only filename was provided.
    let shellHeader = header
    if (!shellHeader && filename) {
        shellHeader = document.createElement("span")
        shellHeader.textContent = filename
    }

    const { container, content } = createPageShell({
        header: shellHeader,
        extraControls: (controls, shell) => {
            if (isGo) {
                const savedHighlighting = localStorage.getItem("code-viewer-highlighting") !== "off"
                const plainBtn = document.createElement("button")
                if (!savedHighlighting) {
                    shell.classList.add("plain-text")
                    plainBtn.classList.add("active")
                }
                plainBtn.textContent = savedHighlighting ? "Highlighting on" : "Highlighting off"
                plainBtn.addEventListener("click", () => {
                    shell.classList.toggle("plain-text")
                    plainBtn.classList.toggle("active")
                    const on = !shell.classList.contains("plain-text")
                    plainBtn.textContent = on ? "Highlighting on" : "Highlighting off"
                    localStorage.setItem("code-viewer-highlighting", on ? "on" : "off")
                })
                controls.appendChild(plainBtn)
            }
        },
    })

    // Render highlighted code.
    const codeWrapper = content
    codeWrapper.innerHTML = renderCode(code, isGo ? "go" : "text")
    // Remove whitespace text nodes between .line spans inside <code>,
    // which <pre> would otherwise preserve as blank lines.
    const codeEl = codeWrapper.querySelector("code")
    if (codeEl) {
        for (const node of Array.from(codeEl.childNodes)) {
            if (node.nodeType === Node.TEXT_NODE) {
                codeEl.removeChild(node)
            }
        }
    }
    // Line click handling.
    let lastClickedLine = 0

    codeWrapper.addEventListener("click", (e) => {
        const target = e.target as HTMLElement
        const line = target.closest<HTMLElement>(".line")
        if (!line) return

        const lineNum = parseInt(line.dataset.line ?? "0", 10)
        if (!lineNum) return

        // Only respond to clicks in the gutter region (left side).
        const rect = line.getBoundingClientRect()
        const gutterWidth = parseFloat(getComputedStyle(line, "::before").width) +
            parseFloat(getComputedStyle(line, "::before").paddingRight) +
            parseFloat(getComputedStyle(line, "::before").marginRight)
        const clickX = e.clientX - rect.left
        if (clickX > gutterWidth) return

        e.preventDefault()
        window.getSelection()?.removeAllRanges()

        if (e.shiftKey && lastClickedLine) {
            const start = Math.min(lastClickedLine, lineNum)
            const end = Math.max(lastClickedLine, lineNum)
            history.replaceState(null, "", `#L${String(start)}-L${String(end)}`)
        } else {
            lastClickedLine = lineNum
            history.replaceState(null, "", `#L${String(lineNum)}`)
        }
        applyHash(false)
    })

    // Hash-based highlighting.
    const applyHash = (scroll: boolean) => {
        codeWrapper.querySelectorAll(".line.highlighted").forEach((el) => {
            el.classList.remove("highlighted")
        })

        const hash = window.location.hash.slice(1)
        if (!hash) return

        const rangeMatch = /^L(\d+)-L(\d+)$/.exec(hash)
        const singleMatch = /^L(\d+)$/.exec(hash)

        let startLine = 0
        let endLine = 0

        if (rangeMatch) {
            startLine = parseInt(rangeMatch[1], 10)
            endLine = parseInt(rangeMatch[2], 10)
        } else if (singleMatch) {
            startLine = endLine = parseInt(singleMatch[1], 10)
        } else {
            return
        }

        for (let i = startLine; i <= endLine; i++) {
            const el = codeWrapper.querySelector(`#L${String(i)}`)
            if (el) el.classList.add("highlighted")
        }

        if (scroll) {
            const first = codeWrapper.querySelector(`#L${String(startLine)}`)
            if (first) {
                first.scrollIntoView({ block: "center" })
            }
        }
    }

    window.addEventListener("hashchange", () => { applyHash(false) })
    requestAnimationFrame(() => { applyHash(true) })

    return container
}
